import { IDropdownOption } from '@fluentui/react';
import { UserJoinedOrgsDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { useOrganizationBranding } from 'features/Header/hooks';
import { ReactElement, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAreaGroupList, useAreaList, useCampus, useIsFavoritesDropdownVisible, useIsL1AreaGroupAvailable } from 'shared-state/directory/hooks';
import { useLocalProfile } from 'shared-state/identity/hooks';
import { useCurrentOrgId } from 'shared-state/location/hook';
import { idpService } from 'services/api/identity-provider.service';
import { getTextColorForBackground, IvicosColors } from 'kits/colorKit';
import { useNavigate, useParams } from 'react-router-dom';
import IvicosStrings from 'kits/language/stringKit';
import { getUrlLocation } from 'kits/urlKit';

export interface ILowerTopBarLink {
    title: string;
    key: string;
    action?: (e: any) => void;
    getSymbol?: () => ReactElement;
    symbol?: string;
}

export interface ILowerTopBarState {
    localProfile: any;
    children?: any;
    joinedOrgs: IDropdownOption[];
    orgId: string | undefined;
    logInAndRedirect: (selectedOrgId: string) => Promise<void>;
    textColor: string;
    backgroundColor: string;
    showJoinedOrgsDropdown: boolean;
    isAnimating: boolean;
    handleDropdownToggle: (content: string) => void;
    l0AreaGroup: any[];
    l1AreaGroup: any[];
    showL0AreaGroupDropdown: boolean;
    showL1AreaGroupDropdown: boolean;
    showAreaDropdown: boolean;
    barTitle: string;
    l0AndL1AGTitle: { l0Title: string; l0AGId: string; l1Title: string; l1AGId: string };
    showL1AGDropdown: boolean;
    handleRedirectionFromDropdown: (areaGroup?: any, isLobby?: boolean, area?: any) => void;
    areaListForDropdown: any;
    currentAreaTitleForDropdown: string;
    dropdownRef: React.RefObject<HTMLDivElement>;
}

type orgsReduced = {
    orgId: string;
    orgName: string;
    orgLogo: string;
    orgColors: string[];
    role: 'user' | 'manager';
};

export const useLowerTopBarState: () => ILowerTopBarState = () => {
    const { groupId, groupLevel, areaId: areaIdParam } = useParams<{ groupId?: string; groupLevel?: string; areaId?: string }>();
    const routerHistory = useNavigate();
    const localProfile = useLocalProfile();
    const [campus] = useCampus();
    const [orgId] = useCurrentOrgId();
    const [branding] = useOrganizationBranding(orgId);
    const [areaGroupList] = useAreaGroupList();
    const [areaList] = useAreaList();
    const [, setIsL1AreaGroupAvailable] = useIsL1AreaGroupAvailable();
    const [isFavoritesDropdownVisible] = useIsFavoritesDropdownVisible();

    const [joinedOrgs, setJoinedOrgs] = useState<IDropdownOption[]>([]);
    const [cacheTimestamp, setCacheTimestamp] = useState<number>(0);
    const [showJoinedOrgsDropdown, setShowJoinedOrgsDropdown] = useState(false);
    const [showL0AreaGroupDropdown, setShowL0AreaGroupDropdown] = useState(false);
    const [showL1AreaGroupDropdown, setShowL1AreaGroupDropdown] = useState(false);
    const [showAreaDropdown, setShowAreaDropdown] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [l0AndL1AGTitle, setL0AndL1AGTitle] = useState({ l0Title: 'LOBBY', l0AGId: '', l1Title: IvicosStrings.areaGroup, l1AGId: '' });
    const [showL1AGDropdown, setShowL1AGDropdown] = useState(false);
    const { isInDashboardLobby, isInDashboardLevel0, isInDashboardLevel1, isInsideArea } = getUrlLocation();

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    const barTitle: string = campus?.name || branding?.orgName || '';
    const dropdownRef = useRef<HTMLDivElement>(null);

    const sortFn = (a: any, b: any) => {
        const aNum = a.sortNumber ?? Infinity;
        const bNum = b.sortNumber ?? Infinity;
        if (aNum !== bNum) return aNum - bNum;
        const nameCompare = a.name.localeCompare(b.name, undefined, { numeric: true });
        if (nameCompare !== 0) return nameCompare;
        return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
    };

    const _l0AreaGroup = useMemo(() => {
        if (!Array.isArray(areaGroupList)) {
            console.warn('areaGroupList is not an array');
            return [];
        }

        return areaGroupList.filter((group) => group.groupLevel === 'L0');
    }, [areaGroupList]);
    const _l1AreaGroup = useMemo(() => {
        if (!Array.isArray(areaGroupList)) {
            console.warn('areaGroupList is not an array');
            return [];
        }

        if (groupLevel === 'L0') {
            return areaGroupList.filter((group) => group.groupLevel === 'L1' && group.parentAreaId === groupId);
        }

        if (groupLevel === 'L1') {
            // get the parentAreaId of the parent
            const parentAreaId = areaGroupList.find((group) => group.id === groupId)?.parentAreaId;
            return areaGroupList.filter((group) => group.groupLevel === 'L1' && group.parentAreaId === parentAreaId);
        }
        if (areaIdParam) {
            const areaLevel = JSON.parse(localStorage.getItem('areaLevel') || '{}');
            if (areaLevel.areaId === areaIdParam && areaLevel.areaLevel === 'L2' && areaLevel.parentAreaId) {
                // get the parentAreaId of the parent
                const parentAreaId = areaGroupList.find((group) => group.id === areaLevel.parentAreaId)?.parentAreaId;
                return areaGroupList.filter((group) => group.groupLevel === 'L1' && group.parentAreaId === parentAreaId);
            }
        }

        return [];
    }, [areaGroupList, groupId, groupLevel, areaIdParam]);

    const areaListForDropdown = useMemo(() => {
        if (!Array.isArray(areaList)) {
            console.warn('areaList is not an array');
            return [];
        }

        if (isInDashboardLobby) {
            return areaList.filter((ar: any) => ar.level === 'L0' && !ar.parentAreaId).sort(sortFn);
        }

        if (isInDashboardLevel0) {
            return areaList.filter((ar: any) => ar.level === 'L1' && ar.parentAreaId === groupId).sort(sortFn);
        }

        if (isInDashboardLevel1) {
            return areaList.filter((ar: any) => ar.level === 'L2' && ar.parentAreaId === groupId).sort(sortFn);
        }

        if (isInsideArea) {
            const areaLevel = JSON.parse(localStorage.getItem('areaLevel') || '{}');
            if (areaLevel.areaId !== areaIdParam) return [];

            if (areaLevel.areaLevel === 'L0' && !areaLevel.parentAreaId) {
                return areaList.filter((ar: any) => ar.level === 'L0' && !ar.parentAreaId).sort(sortFn);
            }

            return areaList.filter((ar: any) => ar.level === areaLevel.areaLevel && ar.parentAreaId === areaLevel.parentAreaId).sort(sortFn);
        }

        return [];
    }, [areaList, groupLevel, groupId, areaIdParam, isInDashboardLobby, isInDashboardLevel0, isInDashboardLevel1, isInsideArea]);

    const currentAreaTitleForDropdown: string = useMemo(() => {
        if (!areaListForDropdown.length || !areaIdParam || groupId || groupLevel) return IvicosStrings.enterOffice;
        const areaLevel = JSON.parse(localStorage.getItem('areaLevel') || '{}');
        const area = areaListForDropdown.find((ar: any) => ar.id === areaLevel.areaId);
        return area?.name;
    }, [areaListForDropdown]);

    const textColor = useMemo(() => {
        return branding?.orgColors?.length ? getTextColorForBackground(branding.orgColors[0]) : '#fff';
    }, [branding]);

    const backgroundColor = useMemo(() => {
        if (branding?.orgColors?.length) {
            return branding.orgColors.length > 1 ? `linear-gradient(45deg,${branding.orgColors.join(',')})` : branding.orgColors[0];
        }
        return IvicosColors.CampusGradient;
    }, [branding]);

    const handleDropdownToggle = (content: string) => {
        setIsAnimating(true);
        content === 'joinedOrgs' && setShowJoinedOrgsDropdown((prev) => !prev); // toggle the dropdown to fold up or down
        content === 'l0AreaGroup' && setShowL0AreaGroupDropdown((prev) => !prev); // toggle the dropdown to fold up or down
        content === 'l1AreaGroup' && setShowL1AreaGroupDropdown((prev) => !prev); // toggle the dropdown to fold up or down
        content === 'areaDropdown' && setShowAreaDropdown((prev) => !prev); // toggle the dropdown to fold up or down
        setTimeout(() => setIsAnimating(false), 300);
    };

    const fetchJoinedOrgs = useCallback(
        async (forceRefresh = false): Promise<IDropdownOption[]> => {
            const now = Date.now();

            // Return cached data if it's still valid and not forcing refresh
            if (!forceRefresh && joinedOrgs.length > 0 && now - cacheTimestamp < CACHE_DURATION) {
                return joinedOrgs;
            }

            let orgsToDisplay: IDropdownOption[] = [];
            try {
                const response: UserJoinedOrgsDto = await idpService.showUserJoinedOrgs();

                if (response.length > 0) {
                    const _response: orgsReduced[] = response; // this is to be able to map through it, due to a typescript issue: https://github.com/microsoft/TypeScript/issues/33591

                    const orgs: IDropdownOption[] = _response.map((org: any) => ({
                        key: org.orgId,
                        text: org.orgName
                    }));
                    orgsToDisplay = orgs;

                    // Update cache
                    setJoinedOrgs(orgsToDisplay);
                    setCacheTimestamp(now);
                }
            } catch (error) {
                console.error(error);
                // Return cached data if available, even if expired, as fallback
                if (joinedOrgs.length > 0) {
                    return joinedOrgs;
                }
            }

            return orgsToDisplay;
        },
        [joinedOrgs, cacheTimestamp]
    );

    useEffect(() => {
        fetchJoinedOrgs();
    }, []);

    const logInAndRedirect = async (selectedOrgId: string) => {
        if (selectedOrgId === orgId || !selectedOrgId) {
            return;
        }

        try {
            await idpService.authUserToOrg(selectedOrgId);
            window.location.href = '/dashboard/areas';
        } catch (err) {
            window.location.href = '/login';
        }
    };

    const l0AreaGroup = useMemo(() => {
        return (
            _l0AreaGroup
                ?.map((group) => ({
                    id: group.id,
                    name: group.name,
                    groupLevel: group.groupLevel,
                    sortNumber: group.sortNumber
                }))
                .sort(sortFn) || []
        );
    }, [_l0AreaGroup]);

    const l1AreaGroup = useMemo(() => {
        return (
            _l1AreaGroup
                ?.map((group) => ({
                    id: group.id,
                    name: group.name,
                    groupLevel: group.groupLevel,
                    sortNumber: group.sortNumber
                }))
                .sort(sortFn) || []
        );
    }, [_l1AreaGroup]);

    useEffect(() => {
        setIsL1AreaGroupAvailable(l1AreaGroup.length > 0);
    }, [l1AreaGroup]);

    useEffect(() => {
        if (isInDashboardLobby) {
            localStorage.setItem('l0AGTitle', 'LOBBY');
            localStorage.setItem('l0AGId', '');
            localStorage.setItem('l1AGTitle', IvicosStrings.areaGroup);
            localStorage.setItem('l1AGId', '');
            setL0AndL1AGTitle({ l0Title: 'LOBBY', l0AGId: '', l1Title: IvicosStrings.areaGroup, l1AGId: '' });
        }

        if (groupId && groupLevel) {
            const l0Title = localStorage.getItem('l0AGTitle') || 'LOBBY';
            const l0AGId = localStorage.getItem('l0AGId') || '';
            const l1Title = groupLevel === 'L0' ? IvicosStrings.areaGroup : localStorage.getItem('l1AGTitle') || IvicosStrings.areaGroup;
            const l1AGId = localStorage.getItem('l1AGId') || '';
            setL0AndL1AGTitle({ l0Title, l0AGId, l1Title, l1AGId });
        }

        if (groupLevel === 'L0' || groupLevel === 'L1') {
            setShowL1AGDropdown(true);
        } else {
            setShowL1AGDropdown(false);
        }
    }, [groupId, groupLevel, isInDashboardLobby]);

    useEffect(() => {
        if (areaIdParam) {
            const l0Title = localStorage.getItem('l0AGTitle') || 'LOBBY';
            const l0AGId = localStorage.getItem('l0AGId') || '';
            const l1Title = localStorage.getItem('l1AGTitle') || IvicosStrings.areaGroup;
            const l1AGId = localStorage.getItem('l1AGId') || '';
            const areaLevel = JSON.parse(localStorage.getItem('areaLevel') || '{}');
            setL0AndL1AGTitle({ l0Title, l0AGId, l1Title, l1AGId });

            if (areaLevel.areaId === areaIdParam && areaLevel.areaLevel === 'L2') {
                setShowL1AGDropdown(true);
            } else {
                setShowL1AGDropdown(false);
            }
        }
    }, [areaIdParam]);

    const handleRedirectionFromDropdown = (item?: any, isLobby?: boolean, type?: 'area' | 'areaGroup' | 'lobby') => {
        if (isLobby && type === 'lobby') {
            localStorage.setItem('l0AGTitle', 'LOBBY');
            localStorage.setItem('l0AGId', '');
            localStorage.setItem('l1AGTitle', IvicosStrings.areaGroup);
            localStorage.setItem('l1AGId', '');
            setL0AndL1AGTitle({ l0Title: 'LOBBY', l0AGId: '', l1Title: IvicosStrings.areaGroup, l1AGId: '' });
            routerHistory('/dashboard');
        }

        if (type === 'area' && item) {
            localStorage.setItem('areaLevel', JSON.stringify({ areaId: item.id, areaLevel: item?.level, parentAreaId: item?.parentAreaId }));
            routerHistory(`/areas/${item.id}`);
        }

        if (type === 'areaGroup' && item) {
            const { id, name, groupLevel } = item ?? {};
            if (groupLevel === 'L0') {
                localStorage.setItem('l0AGTitle', name);
                localStorage.setItem('l0AGId', id);
                routerHistory(`/group/${id}/L0/subAreas`);
            } else if (groupLevel === 'L1') {
                localStorage.setItem('l1AGTitle', name);
                localStorage.setItem('l1AGId', id);
                routerHistory(`/group/${id}/L1/subAreas`);
            }
        }

        setTimeout(() => {
            setShowJoinedOrgsDropdown(false);
            setShowL0AreaGroupDropdown(false);
            setShowL1AreaGroupDropdown(false);
            setShowAreaDropdown(false);
        }, 500);
    };

    useEffect(() => {
        if (isFavoritesDropdownVisible) {
            setTimeout(() => {
                setShowL0AreaGroupDropdown(false);
                setShowL1AreaGroupDropdown(false);
                setShowAreaDropdown(false);
                setShowJoinedOrgsDropdown(false);
            }, 200);
        }
    }, [isFavoritesDropdownVisible]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                // Close dropdown logic here
                setShowJoinedOrgsDropdown(false);
                setShowL0AreaGroupDropdown(false);
                setShowL1AreaGroupDropdown(false);
                setShowAreaDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showJoinedOrgsDropdown, showL0AreaGroupDropdown, showL1AreaGroupDropdown, showAreaDropdown]);

    return {
        localProfile,
        joinedOrgs,
        orgId,
        logInAndRedirect,
        textColor,
        backgroundColor,
        showJoinedOrgsDropdown,
        isAnimating,
        handleDropdownToggle,
        l0AreaGroup,
        l1AreaGroup,
        showL0AreaGroupDropdown,
        showL1AreaGroupDropdown,
        showAreaDropdown,
        barTitle,
        l0AndL1AGTitle,
        showL1AGDropdown,
        handleRedirectionFromDropdown,
        areaListForDropdown,
        currentAreaTitleForDropdown,
        dropdownRef
    };
};
